import React from "react";
import { useNavigate } from "react-router-dom";
import { EyeIcon } from "lucide-react";
import TableComponent from "components/TableComponent";
import { END_POINT } from "constants/apiConstant";
import { formatDateTime } from "utils/helper";

const STATUS_PILL = {
  completed: { label: "Completed", pill: "bg-s360-success-bg text-s360-success-ink", dot: "bg-s360-success" },
  processing: { label: "Processing", pill: "bg-s360-warning-bg text-s360-warning-ink", dot: "bg-s360-warning" },
};
const statusMeta = (status) => STATUS_PILL[status] || STATUS_PILL.processing;

/**
 * Part number search history. These rows are tri_bom_uploads records of
 * type "search", which is why they no longer appear in the uploaded BOM list.
 */
const SearchHistoryTable = ({ refreshTrigger = 0 }) => {
  const navigate = useNavigate();

  const openSearch = (row) => {
    if (!row?.unique_id) return;
    navigate(
      `/search?key=${encodeURIComponent(row.part_number || "")}&unique_id=${row.unique_id}`,
    );
  };

  const columnHeaders = [
    {
      dataField: "part_number",
      name: "Part Number",
      headerAlign: "left",
      style: { textAlign: "left" },
      formatter: (col, row) => (
        <button
          type="button"
          onClick={() => openSearch(row)}
          className="font-mono text-[13px] font-semibold text-s360-violet hover:underline"
          title="View search results"
        >
          {row?.part_number || row?.file_name}
        </button>
      ),
    },
    {
      dataField: "created_at",
      name: "Searched On",
      headerAlign: "left",
      style: { textAlign: "left" },
      formatter: (col, row) => formatDateTime(row?.created_at),
    },
    {
      dataField: "distributor_count",
      name: "Distributors",
      headerAlign: "center",
      style: { textAlign: "center" },
      formatter: (col, row) => (
        <span className="font-mono text-xs text-s360-fg3 tabular-nums">
          {row?.processed_count ?? 0}/{row?.distributor_count ?? 0}
        </span>
      ),
    },
    {
      dataField: "search_status",
      name: "Status",
      headerAlign: "center",
      style: { textAlign: "center" },
      formatter: (col, row) => {
        const meta = statusMeta(row?.search_status);
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-semibold ${meta.pill}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
        );
      },
    },
    {
      dataField: "action",
      name: "Actions",
      headerAlign: "center",
      style: { textAlign: "center" },
      formatter: (col, row) => (
        <div className="flex justify-center">
          <button
            className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-s360-stroke1 text-s360-violet hover:bg-s360-violet-50"
            onClick={() => openSearch(row)}
            title="View search results"
          >
            <EyeIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-bold text-s360-ink">Search history</h2>
        <span className="text-sm text-s360-fg3">
          Your recent part number searches. Open one to load its results again.
        </span>
      </div>
      <div className="bg-white rounded-md shadow-sm overflow-hidden border-t-[3px] border-t-s360-violet">
        <TableComponent
          apiRoute={END_POINT.GET_PART_SEARCH_HISTORY_API}
          columnHeaders={columnHeaders}
          searchPlaceholder="Search by part number…"
          rowHoverClass="hover:bg-s360-bg2"
          refreshTrigger={refreshTrigger}
          noBorderRadius
        />
      </div>
    </div>
  );
};

export default SearchHistoryTable;
